if (!customElements.get("nmn-article")) {
  customElements.define("nmn-article", class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.updateBlogBreadcrumb(signal);
      const content = this.querySelector("[data-article-content]");
      const aside = this.querySelector("[data-article-contents]");
      const list = this.querySelector("[data-article-links]");
      const layout = this.querySelector("[data-article-layout]");
      if (!content) return;

      content.querySelectorAll("table").forEach((table) => {
        if (table.parentElement.matches("[data-article-table]")) return;
        const wrapper = document.createElement("div");
        wrapper.className = "nmn-article__table";
        wrapper.dataset.articleTable = "";
        wrapper.tabIndex = 0;
        table.before(wrapper);
        wrapper.append(table);
      });
      if (!aside || !list || !layout) return;
      aside.hidden = true;
      layout.classList.remove("nmn-article__reading--with-contents");
      list.replaceChildren();
      let headings = [...content.querySelectorAll("h2")].filter((heading) => heading.textContent.trim());
      if (headings.length === 0) {
        headings = [...content.querySelectorAll("h3")].filter((heading) =>
          heading.textContent.trim() && !heading.querySelector("a"));
      }
      if (headings.length < 2) return;

      this.headings = headings;
      this.links = headings.map((heading, index) => {
        if (!heading.id) {
          let id = `${this.id}-heading-${index + 1}`;
          while (document.getElementById(id)) id += "-section";
          heading.id = id;
        }
        const item = document.createElement("li");
        const link = document.createElement("a");
        link.href = `#${encodeURIComponent(heading.id)}`;
        link.textContent = heading.textContent.trim();
        item.append(link);
        list.append(item);
        return link;
      });
      aside.hidden = false;
      layout.classList.add("nmn-article__reading--with-contents");
      const disclosure = this.querySelector("[data-article-disclosure]");
      const desktop = window.matchMedia("(min-width: 750px)");
      const setDisclosure = () => { disclosure.open = desktop.matches; };
      setDisclosure();
      desktop.addEventListener("change", setDisclosure, { signal });
      let previousHeading = null;
      const queueUpdate = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          const offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--nmn-header-height")) || 98;
          let current = headings[0];
          for (const heading of headings) {
            if (heading.getBoundingClientRect().top <= offset + 48) current = heading;
          }
          this.links.forEach((link, index) => {
            if (headings[index] === current) link.setAttribute("aria-current", "location");
            else link.removeAttribute("aria-current");
          });
          // Only move the list when the active section changes, so readers can
          // still scroll the contents manually without it snapping back.
          if (current !== previousHeading && disclosure.open && list.scrollHeight > list.clientHeight) {
            const link = this.links[headings.indexOf(current)];
            const listBounds = list.getBoundingClientRect();
            const linkBounds = link.getBoundingClientRect();
            // Scroll this container alone; scrollIntoView would also move the page.
            if (linkBounds.top < listBounds.top) {
              list.scrollTop += linkBounds.top - listBounds.top;
            } else if (linkBounds.bottom > listBounds.bottom) {
              list.scrollTop += linkBounds.bottom - listBounds.bottom;
            }
          }
          previousHeading = current;
        });
      };
      window.addEventListener("scroll", queueUpdate, { signal, passive: true });
      const refreshContents = () => {
        previousHeading = null;
        queueUpdate();
      };
      window.addEventListener("resize", refreshContents, { signal, passive: true });
      disclosure.addEventListener("toggle", refreshContents, { signal });
      queueUpdate();
    }

    async updateBlogBreadcrumb(signal) {
      const breadcrumb = this.querySelector("[data-article-topic]");
      const tagList = this.querySelector("[data-article-tags]");
      const tags = [...this.querySelectorAll("[data-article-tag]")];
      if (!breadcrumb || !tagList || !tags.length || !this.dataset.blogsUrl) return;

      try {
        // Read the existing navigation so its editable topic list stays authoritative.
        const url = new URL(this.dataset.blogsUrl, window.location.origin);
        if (url.origin !== window.location.origin) return;
        const response = await fetch(url, { signal });
        if (!response.ok) return;
        const document = new DOMParser().parseFromString(await response.text(), "text/html");
        if (signal.aborted) return;
        const topics = [...document.querySelectorAll(".nmn-blog-navigation__link[href]")].map((link) => {
          const topicUrl = new URL(link.getAttribute("href"), url);
          return {
            url: topicUrl,
            slug: decodeURIComponent(topicUrl.pathname.split("/").filter(Boolean).pop() || ""),
            title: link.textContent.trim(),
          };
        }).filter((topic) => topic.url.origin === window.location.origin);
        for (const tag of tags) {
          const topic = topics.find((item) => item.slug === tag.dataset.articleTag);
          if (!topic) continue;
          breadcrumb.href = topic.url.href;
          breadcrumb.textContent = topic.title;
          tags.forEach((item) => { item.hidden = item === tag; });
          tagList.hidden = tags.length === 1;
          break;
        }
      } catch (error) {
        // Keep the server-rendered blog breadcrumb and tags if navigation is unavailable.
        if (error.name !== "AbortError") console.warn("Blog topic navigation could not be loaded.");
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  });
}
